// The record type mark (iOS `IconBadge(systemImage: record.type.systemImage, color: record.type.tint)` and the caps
// `SectionLabel(record.type.title)` above a record's name). Lead orange, Account navy, Contact purple, everywhere a
// store is listed, pinned or charted.

import { Building2, CircleUserRound, MapPin, type LucideIcon } from 'lucide-react';
import { RECORD_TYPE_TITLES, type RecordType } from '@/contracts/enums';
import { RECORD_TYPE_PIN_COLORS } from '@/domain/records';
import { Chip, IconBadge, cn, type ChipSize } from '@/ui';

/** SF Symbols of `RecordType.systemImage` → lucide: mappin.circle, building.2, person.crop.circle. */
export const RECORD_TYPE_ICONS: Readonly<Record<RecordType, LucideIcon>> = {
  lead: MapPin,
  account: Building2,
  contact: CircleUserRound,
};

/** `RecordType.tint` (the same hexes as the record-type pin colors). */
export const RECORD_TYPE_COLORS: Readonly<Record<RecordType, string>> = RECORD_TYPE_PIN_COLORS;

export interface RecordTypeBadgeProps {
  type: RecordType;
  /**
   * - `icon` (default): the tinted rounded square with the type glyph (list rows: 40 px; compact cards: 32 px).
   * - `chip`: a tinted pill "◎ Lead" (tables, headers).
   * - `label`: the caps caption "LEAD" in muted text (above a name).
   */
  variant?: 'icon' | 'chip' | 'label';
  /** Icon square size in px (`icon` variant). */
  size?: number;
  /** Chip size (`chip` variant). */
  chipSize?: ChipSize;
  /**
   * Accessible name of the `icon` variant ("Lead" by default). Pass null when the type is written right next to it so
   * screen readers do not hear it twice.
   */
  label?: string | null;
  className?: string;
}

export function RecordTypeBadge({ type, variant = 'icon', size = 40, chipSize = 'sm', label, className }: RecordTypeBadgeProps) {
  const title = RECORD_TYPE_TITLES[type] ?? 'Store';
  const color = RECORD_TYPE_COLORS[type];
  const Icon = RECORD_TYPE_ICONS[type] ?? MapPin;

  if (variant === 'label') {
    return (
      <span className={cn('text-[11px] leading-4 font-semibold tracking-[0.08em] text-fg-muted uppercase', className)}>
        {title}
      </span>
    );
  }
  if (variant === 'chip') {
    return (
      <Chip color={color} size={chipSize} icon={<Icon aria-hidden />} className={className}>
        {title}
      </Chip>
    );
  }
  const spoken = label === null ? null : (label ?? title);
  return (
    <span
      role={spoken ? 'img' : undefined}
      aria-label={spoken ?? undefined}
      aria-hidden={spoken ? undefined : true}
      title={spoken ?? undefined}
      className={cn('inline-flex shrink-0', className)}
    >
      <IconBadge icon={<Icon strokeWidth={2.2} />} color={color} size={size} />
    </span>
  );
}
